import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {forkJoin, Subscription} from 'rxjs';
import {UtilisateurService} from '../../services/utilisateurs/utilisateur.service';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {UtilisateurBody} from '../../models/utilisateur/UtilisateurBody';
import {Adb2cBodyPatch} from '../../models/adb2c/Adb2cBodyPatch';
import {Adb2cService} from '../../services/adb2c/adb2c.service';
import {environment} from "../../../environments/environment.local";
import {MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition} from '@angular/material/snack-bar';

@Component({
  selector: 'app-espace-utilisateur-dialogue',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatDialogModule, MatDividerModule, MatIconModule, MatSnackBarModule],
  templateUrl: './espace-utilisateur-dialogue.component.html',
  styleUrl: './espace-utilisateur-dialogue.component.scss'
})
export class EspaceUtilisateurDialogueComponent implements OnInit, OnDestroy {
  urlModifierMotDePasse: string = `https://${environment.issuerNameB2c}/${environment.issuerNameOnMicrosoft}/oauth2/v2.0/authorize?p=B2C_1_opus_selfservicepwd&client_id=${environment.idp.clientId}&nonce=defaultNonce&redirect_uri=https%3A%2f%2f${environment.issuerNameB2c}%2f&scope=openid&response_type=code&prompt=login&code_challenge_method=S256&code_challenge=36P67Yu6VOvILt2KvTOSy760ZqsyzUkBc62g4HUZdNI`;
  userId: string = '';
  currentUser: UtilisateurBody | null = null;
  isUserExterne: boolean = false;
  verticalPosition: MatSnackBarVerticalPosition = 'top';
  espaceForm = new FormGroup({
    nom: new FormControl('', Validators.required),
    prenom: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.email]),
    mobilePhone: new FormControl('', [Validators.pattern('^[0-9]{10}$')])
  });
  private userSubscription: Subscription = new Subscription();
  private userUpdateSubscription: Subscription = new Subscription();

  constructor(
    private dialogRef: MatDialogRef<EspaceUtilisateurDialogueComponent>,
    private utilisateurService: UtilisateurService,
    private adb2cService: Adb2cService,
    @Inject(MAT_DIALOG_DATA) public data: { utilisateurConnecteMail: string },
    public snackBar: MatSnackBar
  ) {
  }

  ngOnInit(): void {
    this.loadUserDetails();
    this.espaceForm.get('email')?.disable();
  }

  onSubmit() {
    if (this.espaceForm.invalid) {
      alert('Le formulaire contient des erreurs, veuillez les corriger avant de soumettre !');
      return;
    }

    const formValue = this.espaceForm.getRawValue();
    const utilisateurToUpdate: UtilisateurBody = {
      givenName: formValue.nom!,
      surname: formValue.prenom!,
      displayName: `${formValue.nom} ${formValue.prenom}`,
      mail: formValue.email!,
      mobilePhone: formValue.mobilePhone ?? '',
      accountEnabled: this.currentUser?.accountEnabled!,
      keolisAADAccount: this.currentUser?.keolisAADAccount!,
      entite_id: this.currentUser?.entite_id!,
      userPrincipalName: this.currentUser?.userPrincipalName,
      id_adb2c: this.currentUser?.id_adb2c
    };
    const adb2cToUpdate: Adb2cBodyPatch = {
      givenName: formValue.nom!,
      surname: formValue.prenom!,
      displayName: `${formValue.nom} ${formValue.prenom}`,
      mailNickname: `${formValue.prenom!.charAt(0).toUpperCase()}${formValue.nom}`,
      mobilePhone: formValue.mobilePhone ?? '',
      mail: formValue.email!
    };

    let updates = [];
    if (this.userId) {
      updates.push(this.utilisateurService.updateUser(this.userId, utilisateurToUpdate));
    }

    if (this.currentUser?.id_adb2c) {
      updates.push(this.adb2cService.updateAdb2c(this.currentUser?.id_adb2c, adb2cToUpdate));
    }

    if (updates.length > 0) {
      this.userUpdateSubscription.add(forkJoin(updates).subscribe({
        next: () => {
          console.log('User information updated successfuly');
          this.snackBar.open('Informations utilisateur mises à jour avec succès !', 'Fermer', {
            verticalPosition: this.verticalPosition,
            duration: 5000,
            panelClass: ['snackbar-success']
          });
          this.onClose();
        },
        error: (error) => {
          this.snackBar.open('Erreur lors de la mise à jour des informations utilisateur: ', 'Fermer', {
            verticalPosition: this.verticalPosition,
            duration: 5000,
            panelClass: ['snackbar-error']
          });
          console.error('Error while updating user information', error)
        }
      }));
    }
  }

  onClose() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
    this.userUpdateSubscription.unsubscribe();
  }

  private loadUserDetails() {
    const email = this.data.utilisateurConnecteMail;
    if (email) {
      this.userSubscription.add(
        this.utilisateurService.getUsers({mail: email}).subscribe({
          next: (response) => {
            const user = response.data[0];
            this.currentUser = user;
            this.userId = `${response.data[0].id}`;
            this.isUserExterne = !user.keolisAADAccount;

            this.espaceForm.patchValue({
              nom: user.givenName,
              prenom: user.surname,
              email: user.mail,
              mobilePhone: user.mobilePhone
            });

            if (!this.isUserExterne) {
              this.espaceForm.get('nom')?.disable();
              this.espaceForm.get('prenom')?.disable();
            }
          },
          error: (err) => {
            console.error('Erreur lors de la récupération des détails de l\'utilisateur', err);
          }
        })
      );
    }
  }
}
